import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import styles from './cpRoomUtilization.module.scss';

// ---- room-type display order ------------------------------------------------

const ROOM_TYPE_ORDER = [
  'High Speed 1',
  'High Speed 2',
  'Medium Speed',
  'Low Speed 1',
  'Low Speed 2',
  'Overlay',
] as const;

// ---- room-type color map ----------------------------------------------------

const ROOM_TYPE_COLORS: Record<string, string> = {
  'High Speed 1': '#522d6e',
  'High Speed 2': '#7b4b9f',
  'Medium Speed': '#3b82f6',
  'Low Speed 1':  '#10b981',
  'Low Speed 2':  '#059669',
  'Overlay':      '#f59e0b',
};

// ---- room-type map ----------------------------------------------------------

const ROOM_TYPE_MAP: Record<string, { rooms: string[] }> = {
  'High Speed 1': { rooms: ['Purple', 'Green']      },
  'High Speed 2': { rooms: ['Gold',   'Blue']        },
  'Medium Speed': { rooms: ['Silver']                },
  'Low Speed 1':  { rooms: ['RedC', 'RedD', 'RedB'] },
  'Low Speed 2':  { rooms: ['Black']                 },
  'Overlay':      { rooms: ['WhiteA', 'WhiteB']      },
};

// ---- util bar ---------------------------------------------------------------

function UtilBar({ pct }: { pct: number }) {
  const capped = Math.min(pct, 100);
  const over   = pct > 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: '120px' }}>
      <div style={{
        flex: 1,
        height: '10px',
        background: '#f1f5f9',
        borderRadius: '5px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${capped}%`,
          height: '100%',
          background: over ? '#ef4444' : pct > 80 ? '#f59e0b' : '#10b981',
          borderRadius: '5px',
          transition: 'width 0.3s',
        }} />
      </div>
      <span style={{
        fontSize: '12px',
        fontWeight: 600,
        color: over ? '#ef4444' : pct > 80 ? '#b45309' : '#065f46',
        minWidth: '44px',
        textAlign: 'right',
      }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}

// ---- props ------------------------------------------------------------------

interface CPRoomUtilizationProps {
  selectedMonth: string;
}

// ---- component --------------------------------------------------------------

const CPRoomUtilization = observer(({ selectedMonth }: CPRoomUtilizationProps) => {
  const store = useCapacityStore();
  const { dashboardData } = store;

  if (!dashboardData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
            <i className="pi pi-table" style={{ marginRight: 8 }} />
            Room Utilization
          </span>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          No data available
        </div>
      </div>
    );
  }

  const { rooms, summary, bottles_by_room_type } = dashboardData;
  const params = summary.planning_params;
  const shiftsPerDay      = params['shifts_per_day']       ?? 2;
  const hoursPerShift     = params['hours_per_shift_room'] ?? 7.5;
  const downtimeFactor    = params['downtime_factor']      ?? 0.20;
  const workDays          = dashboardData.crewing_factors[selectedMonth]?.work_days ?? 21;

  const fmtMonth = (iso: string) => {
    const [y, m] = iso.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
  };

  const rows = ROOM_TYPE_ORDER.map((typeName) => {
    const entry    = ROOM_TYPE_MAP[typeName];
    const roomNames = entry?.rooms ?? [];
    const roomCount = roomNames.length;

    // Gross available hours before downtime
    const grossHours = roomCount * shiftsPerDay * hoursPerShift * workDays;
    // Effective available hours net of downtime (e.g. 20% downtime → 80% available)
    const availHours = grossHours * (1 - downtimeFactor);

    const usedHours = roomNames.reduce((sum, roomName) => {
      const room = rooms.find(r => r.room_name === roomName || r.room_short === roomName);
      return sum + (room?.months[selectedMonth]?.line_hours ?? 0);
    }, 0);

    const utilPct = availHours > 0 ? (usedHours / availHours) * 100 : 0;
    const forecastBottles = (bottles_by_room_type?.[typeName]?.[selectedMonth] ?? 0);

    return { typeName, roomCount, usedHours, availHours, utilPct, forecastBottles };
  }).filter(r => r.usedHours > 0 || r.forecastBottles > 0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
          <i className="pi pi-table" style={{ marginRight: 8 }} />
          Room Utilization
        </span>
        <span className={styles.subtitle}>
          {fmtMonth(selectedMonth)} · avail hrs adjusted for {Math.round(downtimeFactor * 100)}% downtime
        </span>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.utilTable}>
          <thead>
            <tr>
              <th className={styles.thLeft}>Room Type</th>
              <th className={styles.thRight}>Forecast Bottles</th>
              <th className={styles.thRight}>Used Hrs</th>
              <th className={styles.thRight}>Avail Hrs</th>
              <th className={styles.thBar}>Utilization</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ typeName, usedHours, availHours, utilPct, forecastBottles }) => (
              <tr key={typeName} className={styles.bodyRow}>
                <td className={styles.tdLeft}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      className={styles.typeChip}
                      style={{ background: ROOM_TYPE_COLORS[typeName] ?? '#94a3b8' }}
                    />
                    {typeName}
                  </span>
                </td>
                <td className={styles.tdRight}>
                  {forecastBottles > 0 ? forecastBottles.toLocaleString() : '—'}
                </td>
                <td className={styles.tdRight}>{usedHours.toFixed(1)}</td>
                <td className={styles.tdRight}>{availHours.toFixed(1)}</td>
                <td className={styles.tdBar}>
                  <UtilBar pct={utilPct} />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No active rooms for this month
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className={styles.footNote}>
        Available hrs = rooms × shifts/day × hrs/shift × work days × (1 − downtime factor)
      </p>
    </div>
  );
});

export default CPRoomUtilization;
